import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { Observable, Subject, filter, takeUntil } from 'rxjs';

import { environment } from 'environments/environment';

import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { UserType } from 'app/core/user/user.types';
import { AddNewComponent } from 'app/layout/common/add-new/add-new.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Common } from 'app/modules/_common/service';
import { Settings } from 'app/modules/_common/settings';
import { ShellType } from 'app/modules/_common/types';
import { TeamCalendarFilterComponent } from 'app/modules/teams/calendar/calendar-filter.component';
import { TeamCalendarService } from 'app/modules/teams/calendar/calendar.service';
import { PersonStatusDialog } from 'app/modules/teams/calendar/person-status-dialog';
//import { PersonStatusDialog } from 'app/modules/teams/my-week/person-status-dialog';
import { WorkdayPlanComponent } from '../common/workday-plan/workday-plan.component';
import { WorkdayPlanService } from '../common/workday-plan/workday-plan.service';

@Component({
    selector     : 'main-layout',
    templateUrl  : './main.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports: [FuseLoadingBarComponent, FuseVerticalNavigationComponent, MatButtonModule, MatIconModule, FuseFullscreenComponent, NgIf, NgClass, RouterOutlet, AsyncPipe, UserComponent, SearchComponent, AddNewComponent, MatMenuModule, TeamCalendarFilterComponent, WorkdayPlanComponent]
})
export class MainLayoutComponent implements OnInit, OnDestroy
{
    @ViewChild(WorkdayPlanComponent) workdayPlanComponent: WorkdayPlanComponent;

    isScreenSmall: boolean;
    navigation: Navigation;
    user = {} as UserType;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    shell$: Observable<ShellType>;
    searchHandler: any;

    version = '' as string;
    public get instanceCode()
    {
        return localStorage.getItem('_ic');
    }

    isHideStatusUserProfile: boolean = false;

    constructor(
        private _userService: UserService,
        private _settings: Settings,
        public dialog: MatDialog,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _changeDetectorRef: ChangeDetectorRef,
        public readonly _router: Router,
        private _service: TeamCalendarService,
        private _workdayPlanService: WorkdayPlanService,
    )
    {
        this.shell$ = Common.shell$;
        this.searchHandler = Common.searchHandler;

        this.version = 'v' + environment.version;

        this._settings.getSettingsPersonStatus$
            .pipe(takeUntil(this._unsubscribeAll), filter((raw) => raw != null))
            .subscribe((data) => {
                data.items.forEach(({ name, value }) => {
                    name = name.toLowerCase();
                    if (name == "ishidestatususerprofile") {
                        this.isHideStatusUserProfile = JSON.parse(value);
                    }
                });

                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to workday plan updates from other components (e.g., preferences)
        this._workdayPlanService.workDayUpdated$
            .pipe(takeUntil(this._unsubscribeAll), filter((raw) => raw != null))
            .subscribe(() => {
                this.onWorkDayUpdated();
            });
    }

    ngOnInit(): void
    {
        this.navigation = {} as Navigation;
        this.navigation.default = [
            {
                id      : 'booking',
                title   : this.instanceCode || 'Employee 365',
                subtitle: '',
                type    : 'group',
                icon    : 'heroicons_outline:document',
                children: [
                        {
                            id   : 'booking.calendar',
                            title: 'Calendar',
                            type : 'basic',
                            icon : 'heroicons_outline:calendar-days',
                            link : '/calendar',
                        },
                        //{
                        //    id   : 'booking.my-week',
                        //    title: 'My Week',
                        //    type : 'basic',
                        //    icon : 'heroicons_outline:calendar-days', //view_week
                        //    link : '/my-week',
                        //},
                        //{
                        //    id   : 'booking.my-team',
                        //    title: 'My Team',
                        //    type : 'basic',
                        //    icon : 'mat_outline:groups',
                        //    link : '/my-team',
                        //},
                        {
                            id   : 'booking.map',
                            title: 'Maps',
                            type : 'basic',
                            icon : 'heroicons_outline:map',
                            link : '/map',
                        },
                        {
                            id   : 'booking.people',
                            title: 'People',
                            type : 'basic',
                            icon : 'heroicons_outline:user-group',
                            link : '/people',
                        },
                        {
                            id   : 'booking.preferences',
                            title: 'Preferences',
                            type : 'basic',
                            icon : 'heroicons_outline:adjustments-horizontal',
                            link : '/preferences',
                        },
                        //{ type: 'spacer' },
                        //{ type: 'spacer' },
                        //{ type: 'spacer' },
                        //{ type: 'spacer' },
                        //{ type: 'spacer' },
                        //{ type: 'spacer'},
                        //{
                        //    id   : 'booking.signout',
                        //    title: 'Sign Out',
                        //    type : 'basic',
                        //    icon : 'heroicons_outline:arrow-right-on-rectangle',
                        //    link : '/sign-out',
                        //}
                ]
            }
        ];

        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: UserType) =>
            {
                this.user = user;
                this._personStatusDisplay = user.personStatusDisplay;
                this._personStatusColour = user.personStatusColour;

                this._changeDetectorRef.markForCheck();
            });

        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) =>
            {
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }


    ngOnDestroy(): void
    {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    toggleNavigation(name: string): void
    {
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation)
        {
            navigation.toggle();

            //this.drawerOpened = navigation.opened;
        }
    }

    _personStatusDisplay: string = '';
    _personStatusColour: string = '';
    openPersonStatus()
    {
        const dialogRef = this.dialog.open(PersonStatusDialog, {
            width: '350px',
            height: '510px',
            autoFocus: true,
            restoreFocus: false,
            hasBackdrop: true
        });

        dialogRef.afterClosed().subscribe(data => {
            if (data) {
                this.user.personStatusDisplay = data.personStatusDisplay;
                this.user.personStatusColour = data.personStatusColour;
                //this._personStatusDisplay = data.personStatusDisplay;
                //this._personStatusColour = data.personStatusColour;

                this._changeDetectorRef.markForCheck();
            }
        });
    }

    onWorkDayUpdated(): void
    {
        // Use setTimeout to ensure ViewChild is available and change detection has run
        setTimeout(() => {
            // Reload workday-plan component
            if (this.workdayPlanComponent) {
                this.workdayPlanComponent.reload();
                this._changeDetectorRef.markForCheck();
            }
        }, 0);
    }

    openWorkdayPlan(): void
    {
        this._router.navigate(['/preferences']);
    }

    getPersonStatusTextColor(): string
    {
        switch(this.user?.personStatusColour?.toLowerCase()){
            case 'yellow':
                return 'text-black';
            default:
                return 'text-white';
        }
    }
}
